import type { ExpenseCategoryDefinition, ExpenseSummary } from '@/types'
import { getCategoryBadge } from './expense-constants'

/**
 * Pure logic behind the expense dashboard (F-FE-N129): the category chips on
 * the ledger, the totals strip for the active filter, and the "Spend by
 * category" rows on Overview and the P&L. All of it reads the summary
 * endpoint, which totals exactly the filter the list is showing (F-RPT-N37),
 * so a chip, a bar and the table can never disagree.
 */

/**
 * P&L granularity (F-FE-N141). The control was wired all along — it switches
 * the SQL bucketing behind the trend chart, the Period breakdown table and the
 * workbook's breakdown sheet — but it sat above six blocks of whole-window
 * figures that never move, and on a short range weekly and monthly collapse to
 * the same single row as daily. So it read as dead. These rules keep it to the
 * grains a range can actually show, and say why when one is unavailable.
 */
export type PnlGranularity = 'hourly' | 'daily' | 'weekly' | 'monthly'

export const PNL_GRANULARITIES: PnlGranularity[] = ['hourly', 'daily', 'weekly', 'monthly']

export const PNL_GRANULARITY_LABELS: Record<PnlGranularity, string> = {
  hourly: 'Hourly',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
}

function isPnlGranularity(g: string): g is PnlGranularity {
  return (PNL_GRANULARITIES as string[]).includes(g)
}

/** The grain that reads best over `days` days. */
export function defaultGranularity(days: number): PnlGranularity {
  if (days <= 1) return 'hourly'
  if (days <= 31) return 'daily'
  if (days <= 186) return 'weekly'
  return 'monthly'
}

/** Why this grain is unavailable for a range of `days` days, or null when it works. */
export function granularityBlockedReason(g: PnlGranularity, days: number): string | null {
  switch (g) {
    case 'hourly':
      // 24 points a day: past a week the chart is unreadable, not wrong.
      return days > 7 ? 'Hourly reads well over a week or less' : null
    case 'weekly':
      return days < 7 ? 'The range is shorter than a week' : null
    case 'monthly':
      return days < 28 ? 'The range is shorter than a month' : null
    default:
      return null
  }
}

/** Keep the operator's choice while the range still supports it; otherwise
 *  fall back to the grain that suits the new range. */
export function resolveGranularity(current: string, days: number): PnlGranularity {
  if (isPnlGranularity(current) && granularityBlockedReason(current, days) == null) return current
  return defaultGranularity(days)
}

export interface CategoryChip {
  slug: string
  label: string
  color: string
  /** Everything filed under the category in the filter. */
  total: number
  count: number
  /** Some or all of that spend is stock (reaches the P&L as COGS, not opex). */
  isStock: boolean
}

type SummaryLike = Pick<ExpenseSummary, 'categories'>

/** One chip per category in the filter, biggest spend first. */
export function buildCategoryChips(summary: SummaryLike | undefined, defs: ExpenseCategoryDefinition[]): CategoryChip[] {
  const cats = summary?.categories ?? []
  return [...cats]
    .sort((a, b) => b.total - a.total || a.category.localeCompare(b.category))
    .map(c => {
      const badge = getCategoryBadge(c.category, defs)
      return {
        slug: c.category,
        label: badge.label,
        color: badge.color,
        total: c.total,
        count: c.count,
        isStock: (c.stock_total ?? 0) > 0.005,
      }
    })
}

export interface FilterTotals {
  /** Operating expenses — what enters the P&L. */
  opex: number
  /** Stock purchases — excluded from opex, reaches the P&L as COGS at sale. */
  stock: number
  count: number
}

/** What the totals strip says. With a category selected it reads that
 *  category's slice of the same summary, which is exactly what the list shows. */
export function filterTotals(summary: ExpenseSummary | undefined, category: string): FilterTotals {
  if (!summary) return { opex: 0, stock: 0, count: 0 }
  if (!category) {
    return { opex: summary.grand_total ?? 0, stock: summary.stock_total ?? 0, count: summary.count ?? 0 }
  }
  const c = summary.categories.find(x => x.category === category)
  if (!c) return { opex: 0, stock: 0, count: 0 }
  const stock = c.stock_total ?? 0
  return { opex: c.total - stock, stock, count: c.count }
}

export interface SpendRow extends CategoryChip {
  /** Bar length as a % of the biggest category. */
  barPct: number
  /** Share of all positive spend in the period. */
  sharePct: number
  /** A negative total — a supplier return credited back — is not spend. */
  isCredit: boolean
}

const round1 = (n: number) => Math.round(n * 10) / 10

/** Rows for the "Spend by category" list: biggest first, sized for a bar. */
export function spendRows(summary: SummaryLike | undefined, defs: ExpenseCategoryDefinition[]): SpendRow[] {
  const chips = buildCategoryChips(summary, defs)
  const positive = chips.filter(c => c.total > 0)
  const max = positive.reduce((m, c) => Math.max(m, c.total), 0)
  const sum = positive.reduce((s, c) => s + c.total, 0)
  return chips.map(c => {
    const isCredit = c.total < 0
    return {
      ...c,
      isCredit,
      barPct: isCredit || max <= 0 ? 0 : round1((c.total / max) * 100),
      sharePct: isCredit || sum <= 0 ? 0 : round1((c.total / sum) * 100),
    }
  })
}
